import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { api } from "../config/Api";
import { RolesDto, User } from '../types/userType';
import { AxiosError } from "axios";

interface LoginRequest {
  email: string;
  otp: string;
}
interface SignUpRequest {
  email: string;
  otp: string;
  fullName: string;
  mobile: string
}
export interface LoginOtpRequest {
  email: string,
  role: string,
  isSignup: boolean
}
export const sendLoginSignupOtp = createAsyncThunk<any, LoginOtpRequest>(
  "/auth/sendLoginSignupOtp",
  async (req: LoginOtpRequest, { rejectWithValue }) => {
    try {
   
      const response = await api.post("/auth/sent/login-signup-otp", req);
    
    } catch (error: any) {
      console.error("Error: ", error?.response?.data?.message);
      const axiosError = error as AxiosError<{ message: string }>
      // need return statement so we can trigger rejectWithValue because If the Redux state updates to fulfilled (instead of rejected), the try block inside the thunk does not trigger rejectWithValue, meaning the catch block isn't entering into a "failure" state.
      //Update your thunk to strictly return rejectWithValue when an API error occurs.
      return rejectWithValue(axiosError.response?.data?.message || 'Sent Login And Signup OTP Failed')
    }
  }
);
//  createAsyncThunk<LoginResponse, LoginRequest>

export const signIn = createAsyncThunk<any, LoginRequest>(
  "/auth/signIn",
  async (loginRequest, { rejectWithValue }) => {
    try {
      const response = await api.post("/auth/signin", loginRequest);
      
    } catch (error: any) {
      console.error("Error: ", error?.response?.data?.message);
      const axiosError = error as AxiosError<{ message: string }>
      return rejectWithValue(axiosError.response?.data?.message || 'Login Failed')
    }
  }
);

export const signUp = createAsyncThunk<any, SignUpRequest>(
  "/auth/signUp",
  async (signUpRequest, { rejectWithValue }) => {
    try {
      const response = await api.post("/auth/signup", signUpRequest);
  
    } catch (error) {
      console.error("Error: ", error);
      const axiosError = error as AxiosError<{ message: string }>
      return rejectWithValue(axiosError.response?.data?.message || 'Signup Failed')
    }
  }
);

export const fetchUserProfile = createAsyncThunk<User>(
  "/user/fetchUserProfile",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/user/profile");
      
      return response.data
    } catch (error) {
      console.error("Error: ", error);
    }
  }
);


export const logout = createAsyncThunk<any, any>(
  "/auth/logout",
  async (navigate, { rejectWithValue }) => {
   
    try {
      await api.post("/auth/logout");
    
      navigate('/');

    } catch (error) {
      console.error(error);
    }
  }
);

export const validateSession = createAsyncThunk<RolesDto>(
  "/auth/validateSession",
  async (_, { rejectWithValue }) => {
    try {
     
      const response = await api.get("/auth/validate-session", 
      {meta:{silent:true}}
    );
      
      return response.data as RolesDto; // Return the session validity
    } catch (error) {
      console.error("Session validation failed:", error);
      return rejectWithValue(false); // If validation fails, session is invalid
    }
  }
);

interface AuthState {
  otpSent: boolean;
  isLoggedIn: boolean;
  roles:string[]|null;
  user: User | null;
  loading: boolean;

};

const initialState: AuthState = {
  otpSent: false,
  roles:null,
  isLoggedIn: false,
  user: null,
  loading: true,

}

// The loading state should initially be true because it reflects that the app is in the process of determining the user's authentication state. Here's why:

// Asynchronous API Call:

// When your app loads, it needs to fetch the user's data (e.g., from an API or local storage). This is an asynchronous process.
// During this process, the loading flag ensures the app knows it's waiting for the data to resolve.
// Prevent Premature Redirects:

// If loading is initially false, the app might incorrectly assume the user is unauthenticated or doesn't have the required role, causing unnecessary redirects before the actual user data is fetched.
// By setting loading to true initially, the app can wait until the user data is loaded and then decide whether to grant or deny access.
// User Experience:

// Showing a loading spinner or some indication of progress while fetching user data improves the user experience, especially in scenarios where the API call takes some time.

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(sendLoginSignupOtp.pending, (state) => {
      state.loading = true
      state.otpSent = false
    })
    builder.addCase(sendLoginSignupOtp.fulfilled, (state) => {
      state.loading = false
      state.otpSent = true
    })
    builder.addCase(sendLoginSignupOtp.rejected, (state) => {
      state.loading = false
      state.otpSent = false
    })
    builder.addCase(signIn.fulfilled, (state, action) => {
      state.isLoggedIn = true
      state.otpSent = false
    })
    builder.addCase(signUp.fulfilled, (state, action) => {
      state.isLoggedIn = true
    })
    builder.addCase(signUp.pending, (state, action) => {
      state.loading = true
    })
    builder.addCase(signUp.rejected, (state, action) => {
      state.loading = false;
      state.otpSent=false
    })
    builder.addCase(fetchUserProfile.pending, (state) => {

      state.loading = true
    })

    builder.addCase(fetchUserProfile.fulfilled, (state, action) => {
      state.user = action.payload
      state.isLoggedIn = true
      state.loading = false
    })

    builder.addCase(fetchUserProfile.rejected, (state, action) => {
      state.isLoggedIn = false
      state.loading = false
    })
    builder.addCase(logout.fulfilled, (state, action) => {
      state.user = null
      state.isLoggedIn = false
      state.loading = false
      state.otpSent = false

    })
      .addCase(validateSession.pending, (state) => {
        state.isLoggedIn = false;
        state.loading = true
      })
      // Add validateSession logic
      .addCase(validateSession.fulfilled, (state, action) => {
        const payload = action.payload as RolesDto | undefined;
        if (payload?.authenticated == true) {
          state.loading = false
          state.isLoggedIn = true;
          state.roles = action.payload.roles??[]
        } else {
          state.isLoggedIn = false
          state.roles=[]
        }
        state.loading=false
      })

      .addCase(validateSession.rejected, (state) => {
        state.isLoggedIn = false; // Set to false if session validation fails
        state.otpSent = false
        state.loading=false
      });
  }

})

export const authSliceReducer = authSlice.reducer